import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const LifecycleStageDBSchema = z.enum(['NEW', 'ACTIVE', 'REPEAT', 'AT_RISK', 'DORMANT', 'CHURNED']);
export type LifecycleStageDB = z.infer<typeof LifecycleStageDBSchema>;

export const VendorHealthGradeSchema = z.enum(['A', 'B', 'C', 'D', 'F']);
export type VendorHealthGrade = z.infer<typeof VendorHealthGradeSchema>;

export const FraudEntityTypeSchema = z.enum([
  'USER',
  'VENDOR',
  'ORDER',
  'PAYMENT',
  'REFUND',
  'WALLET',
  'WITHDRAWAL',
  'REFERRAL',
  'COUPON',
]);
export type FraudEntityType = z.infer<typeof FraudEntityTypeSchema>;

export const FraudSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export type FraudSeverity = z.infer<typeof FraudSeveritySchema>;

export const FraudSignalStatusSchema = z.enum([
  'OPEN',
  'ACKNOWLEDGED',
  'ACTION_TAKEN',
  'DISMISSED',
  'FALSE_POSITIVE',
]);
export type FraudSignalStatus = z.infer<typeof FraudSignalStatusSchema>;

export const PricingSignalKindSchema = z.enum([
  'STOCK_PRESSURE',
  'DEMAND_SPIKE',
  'COMPETITION',
  'REGIONAL',
  'CLEARANCE',
  'FLASH',
]);
export type PricingSignalKind = z.infer<typeof PricingSignalKindSchema>;

export const PricingSignalStatusSchema = z.enum(['PROPOSED', 'APPLIED', 'REJECTED', 'EXPIRED']);
export type PricingSignalStatus = z.infer<typeof PricingSignalStatusSchema>;

export const ProductInteractionKindSchema = z.enum([
  'IMPRESSION',
  'CLICK',
  'VIEW',
  'ADD_TO_CART',
  'REMOVE_FROM_CART',
  'WISHLIST',
  'PURCHASE',
  'REVIEW',
]);
export type ProductInteractionKind = z.infer<typeof ProductInteractionKindSchema>;

// ── 360 views ──────────────────────────────────────────────────────────────

export const Customer360Schema = z.object({
  userId: Id,
  totalOrders: z.number().int().nonnegative(),
  totalSpendPaise: Paise,
  avgOrderValuePaise: Paise,
  repeatRateBps: z.number().int(),
  churnRiskBps: z.number().int(),
  lifecycleStage: LifecycleStageDBSchema,
  firstOrderAt: IsoDateTime.nullable(),
  lastOrderAt: IsoDateTime.nullable(),
  lastSeenAt: IsoDateTime.nullable(),
  preferredCategoryIds: z.array(z.string()),
  segments: z.array(z.string()),
  computedAt: IsoDateTime,
});
export type Customer360 = z.infer<typeof Customer360Schema>;

export const Vendor360Schema = z.object({
  vendorId: Id,
  gmvLifetimePaise: Paise,
  gmv30dPaise: Paise,
  totalOrders: z.number().int().nonnegative(),
  deliveredOrders: z.number().int().nonnegative(),
  returnedOrders: z.number().int().nonnegative(),
  returnRateBps: z.number().int(),
  fulfillmentRateBps: z.number().int(),
  avgFulfillmentDays: z.number().int(),
  slaBps: z.number().int(),
  healthGrade: VendorHealthGradeSchema,
  lastOrderAt: IsoDateTime.nullable(),
  lastSettledAt: IsoDateTime.nullable(),
  computedAt: IsoDateTime,
});
export type Vendor360 = z.infer<typeof Vendor360Schema>;

export const Product360Schema = z.object({
  productId: Id,
  vendorId: Id,
  categoryId: Id,
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  addToCarts: z.number().int().nonnegative(),
  orders: z.number().int().nonnegative(),
  conversionBps: z.number().int(),
  ctrBps: z.number().int(),
  avgRatingX100: z.number().int(),
  ratingCount: z.number().int(),
  returnRateBps: z.number().int(),
  revenueLifetimePaise: Paise,
  daysToFirstSale: z.number().int().nullable(),
  stockOnHand: z.number().int().nonnegative(),
  lastSoldAt: IsoDateTime.nullable(),
  computedAt: IsoDateTime,
});
export type Product360 = z.infer<typeof Product360Schema>;

// ── Fraud signals ──────────────────────────────────────────────────────────

export const FraudSignalSchema = z.object({
  id: Id,
  entityType: FraudEntityTypeSchema,
  entityId: z.string(),
  kind: z.string(),
  severity: FraudSeveritySchema,
  score: z.number().int().min(0).max(100),
  status: FraudSignalStatusSchema,
  context: z.unknown().nullable(),
  resolvedAt: IsoDateTime.nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type FraudSignal = z.infer<typeof FraudSignalSchema>;

export const FlagFraudRequest = z
  .object({
    entityType: FraudEntityTypeSchema,
    entityId: z.string().trim().min(1).max(64),
    kind: z.string().trim().min(2).max(80),
    score: z.number().int().min(0).max(100),
    severity: FraudSeveritySchema.optional(),
    context: z.record(z.unknown()).optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();
export type FlagFraudRequest = z.infer<typeof FlagFraudRequest>;

export const ResolveFraudSignalRequest = z
  .object({
    status: z.enum(['ACKNOWLEDGED', 'ACTION_TAKEN', 'DISMISSED', 'FALSE_POSITIVE']),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ResolveFraudSignalRequest = z.infer<typeof ResolveFraudSignalRequest>;

// ── Pricing signals ────────────────────────────────────────────────────────

export const PricingSignalSchema = z.object({
  id: Id,
  variantId: Id,
  kind: PricingSignalKindSchema,
  suggestedDiscountBps: z.number().int(),
  confidenceBps: z.number().int(),
  status: PricingSignalStatusSchema,
  validUntil: IsoDateTime.nullable(),
  context: z.unknown().nullable(),
  appliedAt: IsoDateTime.nullable(),
  rejectedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type PricingSignal = z.infer<typeof PricingSignalSchema>;

export const ProposePricingSignalRequest = z
  .object({
    variantId: Id,
    kind: PricingSignalKindSchema,
    suggestedDiscountBps: z.number().int().min(0).max(9000),
    confidenceBps: z.number().int().min(0).max(10000).default(0),
    validUntil: IsoDateTime.optional(),
    context: z.record(z.unknown()).optional(),
  })
  .strict();
export type ProposePricingSignalRequest = z.infer<typeof ProposePricingSignalRequest>;

export const ResolvePricingSignalRequest = z
  .object({
    decision: z.enum(['APPLY', 'REJECT']),
  })
  .strict();
export type ResolvePricingSignalRequest = z.infer<typeof ResolvePricingSignalRequest>;

// ── Product interaction events ─────────────────────────────────────────────

export const RecordInteractionRequest = z
  .object({
    productId: Id,
    variantId: Id.optional(),
    kind: ProductInteractionKindSchema,
    anonymousId: z.string().trim().max(120).optional(),
    source: z.string().trim().max(120).optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecordInteractionRequest = z.infer<typeof RecordInteractionRequest>;

// ── ETL ────────────────────────────────────────────────────────────────────

export const RunEtlRequest = z
  .object({
    /// Limit per scope. Default 1000.
    limit: z.number().int().min(1).max(10_000).default(1000),
    /// What to refresh. Defaults to all three.
    scopes: z.array(z.enum(['customer', 'vendor', 'product'])).optional(),
  })
  .strict();
export type RunEtlRequest = z.infer<typeof RunEtlRequest>;

export const EtlRunResultSchema = z.object({
  customersRefreshed: z.number().int().nonnegative(),
  vendorsRefreshed: z.number().int().nonnegative(),
  productsRefreshed: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type EtlRunResult = z.infer<typeof EtlRunResultSchema>;
